import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Tooltip,
  IconButton,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  Collapse,
} from '@mui/material';
import { format } from 'date-fns';
import { useImageUrls } from '../hooks/useImageUrls';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { useTranslation } from 'react-i18next';
import { fi, enUS } from 'date-fns/locale';
import { ThemeProvider } from '@mui/material/styles';
import NodeTheme from '../resources/NodeTheme';

import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchBar from './SearchBar';
import DocumentGridItem from './DocumentGridItem';
import FullSizePreview from './FullSizePreview';
import { DiaryItem } from '../hooks/useDiaryItems';
import { parseHighlightedText } from './SearchBar';
import useActiveUser from '../collaboration/useActiveUser';

interface SearchMessage {
  id: string;
  thread_id: string;
  content: string;
  user_id: string;
  user_email: string;
  user_name: string;
  created_at: string;
  modified_at: string;
  role: string;
  subtype?: string;
  diary_id: string;
  threadTitle?: string;
  highlightedContent: string;
  highlightedTitle?: string;
}

interface DocumentsTableProps {
  documents: DiaryItem[];
  onRowClick: (document: DiaryItem) => void;
  refetch: () => void;
  isRefetching: boolean;
  isLoading?: boolean;
}

type SortField = 'date' | 'title' | 'category' | 'status' | 'amount';
type SortOrder = 'asc' | 'desc';

const CATEGORIES: Record<string, string> = {
  expense: 'Expenses',
  income: 'Sales',
  'bank statement': 'Bank Statements',
  other: 'Documents',
};

type DateKey = {
  dayLabel?: 'unknown' | 'today' | 'yesterday';
  daysAgo?: number;
  month?: number;
  year?: number;
};

// Group dates
const getDateKey = (date: number | string | undefined): DateKey => {
  if (!date) return { dayLabel: 'unknown' };

  // Create dates and reset time parts for accurate day comparison
  const itemDate = new Date(date);
  itemDate.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Get time values for comparison
  const todayTime = today.getTime();
  const itemTime = itemDate.getTime();

  // Calculate exact day difference - use floor instead of round
  const daysDiff = Math.floor((todayTime - itemTime) / (24 * 60 * 60 * 1000));

  // Check if it's today
  if (daysDiff === 0) {
    return { dayLabel: 'today' };
  }

  // Check if it's yesterday
  if (daysDiff === 1) {
    return { dayLabel: 'yesterday' };
  }

  // Check if it's within the last week
  if (daysDiff <= 7) {
    return { daysAgo: daysDiff };
  }

  // For older dates, use month and year
  return { month: itemDate.getMonth() + 1, year: itemDate.getFullYear() };
};

const DocumentsTable: React.FC<DocumentsTableProps> = ({
  documents,
  onRowClick,
  refetch,
  isRefetching,
  isLoading = false,
}) => {
  const { t, i18n } = useTranslation();
  const { activeUser } = useActiveUser();
  const [sortConfig] = useState<{
    field: SortField;
    order: SortOrder;
  }>({
    field: 'date',
    order: 'desc',
  });
  const [filters, setFilters] = useState({
    category: '',
    month: '',
    year: '',
    status: '',
  });
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchMessage[]>([]);
  const [isSearchActive, setIsSearchActive] = useState(false);

  // Memoize monthNames based on language changes
  const monthNames = useMemo(
    () => [
      t('January'),
      t('February'),
      t('March'),
      t('April'),
      t('May'),
      t('June'),
      t('July'),
      t('August'),
      t('September'),
      t('October'),
      t('November'),
      t('December'),
    ],
    [t]
  );

  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isLoadingFullSize, setIsLoadingFullSize] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<DiaryItem | null>(
    null
  );

  // Memoize the getValidDate function to avoid recreating it on every render
  const getValidDate = useCallback((doc: DiaryItem) => {
    const timestamp = doc.timestamp || doc.createdAt;
    if (!timestamp) return new Date();

    // If timestamp is in seconds (10 digits), convert to milliseconds
    const timestampNum =
      typeof timestamp === 'number' ? timestamp : Number(timestamp);
    if (timestampNum && timestampNum.toString().length === 10) {
      return new Date(timestampNum * 1000);
    }
    return new Date(timestamp);
  }, []);

  // Memoize years calculation
  const years = useMemo(
    () =>
      [
        ...new Set(documents.map((doc) => getValidDate(doc).getFullYear())),
      ].sort((a, b) => b - a),
    [documents, getValidDate]
  );

  // Memoize months array
  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), []);

  // Memoize the getAmount function
  const getAmount = useCallback((document: DiaryItem) => {
    if (!document.interpretationObj) return '-';
    try {
      const interpretation =
        typeof document.interpretationObj === 'string'
          ? JSON.parse(document.interpretationObj)
          : document.interpretationObj;
      return interpretation.totalIncludingVAT
        ? `€${interpretation.totalIncludingVAT.toFixed(2)}`
        : '-';
    } catch {
      return '-';
    }
  }, []);

  // Memoize the isDocumentUnread function
  const isDocumentUnread = useCallback(
    (document: DiaryItem) => {
      // Check if item has unread messages
      // No need to check unread status if there's no user or no message
      if (!activeUser?.id || !document.lastAssistantMessage) {
        return false;
      }

      // Add a buffer time (3 seconds) to account for timestamp synchronization issues
      const BUFFER_TIME = 3000; // 3 seconds in milliseconds

      // Parse last_read_data if it's a string from database
      let lastReadData = null;
      try {
        if (document.last_read_data) {
          // Handle both string and object formats
          if (typeof document.last_read_data === 'string') {
            lastReadData = JSON.parse(document.last_read_data);
          } else {
            lastReadData = document.last_read_data;
          }
        }
      } catch (error) {
        console.error('Error parsing last_read_data:', error);
        return true; // If we can't parse it, consider it unread
      }

      // Check if this user has read this item using the structured data
      const userHasReadItem =
        lastReadData && lastReadData.user_id === activeUser.id;
      const userLastRead = userHasReadItem ? lastReadData.timestamp : undefined;

      // If never read before by this user, it's unread
      if (!userHasReadItem || userLastRead === undefined) {
        return true;
      }

      // Only check message timestamps, not modified_at
      const msgTime: number | undefined =
        document?.lastAssistantMessageTimestamp;

      // Only consider it unread if there are new messages
      if (msgTime && userLastRead && msgTime > userLastRead + BUFFER_TIME) {
        return true;
      }

      return false;
    },
    [activeUser?.id]
  );

  // Prepare input for useImageUrls
  const imageSources = useMemo(
    () =>
      documents
        .map((doc) => {
          const content = doc.content?.[0];
          return content && doc.id && content.hash
            ? {
                id: doc.id,
                hash: content.hash,
                path: content.path,
                thumbnail: content.thumbnail,
              }
            : null;
        })
        .filter(Boolean) as Array<{
        id: string;
        hash: string;
        path?: string;
        thumbnail?: string;
      }>,
    [documents]
  );

  // Use the shared hook for image loading
  const imageUrls = useImageUrls(imageSources);

  // Get date-fns locale based on current language
  const dateLocale = i18n.language === 'fi' ? fi : enUS;

  // handleSort reserved for future table sorting controls

  // Handle search result click
  const handleSearchResultClick = useCallback(
    (result: SearchMessage) => {
      // Find the corresponding document by diary_id
      const document = documents.find((doc) => doc.id === result.diary_id);
      if (document) {
        onRowClick(document);
      }
    },
    [documents, onRowClick]
  );

  // Handle search results change
  const handleSearchResultsChange = useCallback(
    (results: SearchMessage[], query: string) => {
      setSearchResults(results);
      setSearchQuery(query);
      setIsSearchActive(query.length > 0 && results.length > 0);
    },
    []
  );

  // Enhanced documents with highlighted content from search results
  const enhancedDocuments = useMemo(() => {
    if (!isSearchActive || searchResults.length === 0) {
      return documents;
    }

    // Create a map of search results by diary_id for quick lookup
    const searchResultsMap = new Map<string, SearchMessage>();
    searchResults.forEach((result) => {
      searchResultsMap.set(result.diary_id, result);
    });

    // Enhance documents with highlighted content
    return documents.map((doc) => {
      const searchResult = searchResultsMap.get(doc.id);
      if (searchResult) {
        return {
          ...doc,
          highlightedTitle: searchResult.highlightedTitle,
          highlightedContent: searchResult.highlightedContent,
        };
      }
      return doc;
    });
  }, [documents, searchResults, isSearchActive]);

  // Memoize the filtered and sorted documents
  const sortedDocuments = useMemo(() => {
    let filtered = [...enhancedDocuments];

    // Apply search query - if we have search results, filter by those diary IDs
    if (searchQuery && isSearchActive && searchResults.length > 0) {
      const searchResultIds = new Set(
        searchResults.map((result) => result.diary_id)
      );
      filtered = filtered.filter((doc) => searchResultIds.has(doc.id));
    } else if (searchQuery && !isSearchActive) {
      // Fallback to local search if no search results
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((doc) => {
        const title = doc.title?.toLowerCase() || '';

        // Match the displayText logic from DocumentGridItem
        let displayText = '';
        if (doc.type === 'NewChat') {
          displayText = doc.lastAssistantMessage
            ? doc.lastAssistantMessage
            : doc.description || t('No description');
        } else {
          displayText =
            (doc.lastAssistantMessage ? doc.lastAssistantMessage : null) ||
            doc.description ||
            t('No description');
        }

        return (
          title.includes(query) || displayText.toLowerCase().includes(query)
        );
      });
    }

    // Apply filters
    if (filters.category) {
      filtered = filtered.filter((doc) =>
        filters.category === 'other'
          ? !doc.category
          : doc.category === filters.category
      );
    }

    if (filters.month) {
      filtered = filtered.filter(
        (doc) => getValidDate(doc).getMonth() + 1 === Number(filters.month)
      );
    }

    if (filters.year) {
      filtered = filtered.filter(
        (doc) => getValidDate(doc).getFullYear() === Number(filters.year)
      );
    }

    if (filters.status) {
      filtered = filtered.filter(
        (doc) => (doc.status || 'NEW') === filters.status
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const multiplier = sortConfig.order === 'asc' ? 1 : -1;
      const aDate = getValidDate(a);
      const bDate = getValidDate(b);

      switch (sortConfig.field) {
        case 'date':
          return (aDate.getTime() - bDate.getTime()) * multiplier;

        case 'title':
          return (a.title || '').localeCompare(b.title || '') * multiplier;
        case 'category':
          return (
            (CATEGORIES[a.category || 'other'] || '').localeCompare(
              CATEGORIES[b.category || 'other'] || ''
            ) * multiplier
          );
        case 'status':
          return (a.status || '').localeCompare(b.status || '') * multiplier;
        case 'amount':
          const amountA = parseFloat(getAmount(a).replace('€', '')) || 0;
          const amountB = parseFloat(getAmount(b).replace('€', '')) || 0;
          return (amountA - amountB) * multiplier;
        default:
          return 0;
      }
    });

    return filtered;
  }, [
    enhancedDocuments,
    searchQuery,
    searchResults,
    isSearchActive,
    filters,
    sortConfig,
    getValidDate,
    getAmount,
    t,
  ]);

  const handleImageOpen = useCallback(
    async (document: DiaryItem) => {
      const photoContent = document.content?.[0];
      if (!photoContent?.path || !photoContent?.hash) return;

      setSelectedDocument(document);
      // Show thumbnail first if available
      setSelectedImage(imageUrls[document.id]?.thumbnail || null);
      setIsLoadingFullSize(true);

      // Always try to load the full-size image
      try {
        if (typeof imageUrls.loadFullSize === 'function') {
          // Remove .thumbnail.jpg if present in path
          let originalPath = photoContent.path;
          if (originalPath.endsWith('.thumbnail.jpg')) {
            originalPath = originalPath.replace('.thumbnail.jpg', '.jpg');
          }
          const url = await imageUrls.loadFullSize({
            id: document.id,
            hash: photoContent.hash,
            path: originalPath,
            thumbnail: photoContent.thumbnail,
          });
          if (url) {
            setSelectedImage(url);
          }
        } else {
          setSelectedImage(imageUrls[document.id]?.thumbnail || null);
        }
      } catch (e) {
        setSelectedImage(imageUrls[document.id]?.thumbnail || null);
      } finally {
        setIsLoadingFullSize(false);
      }
    },
    [imageUrls]
  );

  const handleImageClose = useCallback(() => {
    setSelectedImage(null);
    setSelectedDocument(null);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters({
      category: '',
      month: '',
      year: '',
      status: '',
    });
  }, []);

  const isPDF = useCallback((path?: string): boolean => {
    if (!path) return false;
    return path.toLowerCase().endsWith('.pdf');
  }, []);

  // Force grid view on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 600) {
        setViewMode('grid');
      }
    };

    // Initial check
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Disable view mode toggle on mobile
  const handleViewModeChange = useCallback(
    (
      _event: React.MouseEvent<HTMLElement>,
      newMode: 'table' | 'grid' | null
    ) => {
      if (newMode !== null && window.innerWidth >= 600) {
        setViewMode(newMode);
      }
    },
    []
  );

  // Function to group documents by date
  const getGroupedDocuments = useCallback(
    (docs: DiaryItem[]) => {
      const grouped = docs.reduce(
        (
          acc: {
            [key: string]: { key: string; label: string; items: DiaryItem[] };
          },
          doc: DiaryItem
        ) => {
          const dateKey = getDateKey(doc.timestamp || doc.createdAt);
          const keyString = JSON.stringify(dateKey); // Use string representation as object can't be used as key

          let label = '';
          if (dateKey.dayLabel === 'today') label = t('Today');
          else if (dateKey.dayLabel === 'yesterday') label = t('Yesterday');
          else if (dateKey.dayLabel === 'unknown') label = t('Unknown Date');
          else if (dateKey.daysAgo !== undefined)
            label = `${dateKey.daysAgo} ${t('days ago')}`;
          else if (dateKey.month !== undefined && dateKey.year !== undefined)
            label = `${monthNames[dateKey.month - 1]} ${dateKey.year}`;

          if (!acc[keyString]) {
            acc[keyString] = {
              key: keyString,
              label,
              items: [doc],
            };
          } else {
            acc[keyString].items.push(doc);
          }
          return acc;
        },
        {}
      );

      // Convert to array and sort by date (most recent first)
      return Object.values(grouped).sort((a, b) => {
        const aFirstDoc = a.items[0];
        const bFirstDoc = b.items[0];
        const aDate = getValidDate(aFirstDoc);
        const bDate = getValidDate(bFirstDoc);
        return bDate.getTime() - aDate.getTime();
      });
    },
    [t, monthNames, getValidDate]
  );

  // Memoize the grouped documents
  const groupedDocuments = useMemo(
    () => getGroupedDocuments(sortedDocuments),
    [sortedDocuments, getGroupedDocuments]
  );

  // Add scroll handler
  const scrollToMonth = useCallback(
    (targetMonth: number, targetYear: number) => {
      // Find all group elements in the DOM that match the target month/year
      const groupElements = document.querySelectorAll('[data-group-key]');
      const relevantGroups: { element: HTMLElement; date: Date }[] = [];

      groupElements.forEach((element) => {
        const groupKey = element.getAttribute('data-group-key');
        if (!groupKey) return;

        try {
          const dateKey = JSON.parse(groupKey);

          // For month-year groups, check direct match
          if (
            dateKey.month === targetMonth + 1 &&
            dateKey.year === targetYear
          ) {
            const date = new Date(targetYear, targetMonth);
            date.setHours(0, 0, 0, 0);
            relevantGroups.push({ element: element as HTMLElement, date });
            return;
          }

          // For day-based groups (today, yesterday, N days ago), check their actual dates
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          let groupDate;

          if (dateKey.dayLabel === 'today') {
            groupDate = today;
          } else if (dateKey.dayLabel === 'yesterday') {
            groupDate = new Date(today);
            groupDate.setDate(today.getDate() - 1);
          } else if (dateKey.daysAgo !== undefined) {
            groupDate = new Date(today);
            groupDate.setDate(today.getDate() - dateKey.daysAgo);
          }

          if (
            groupDate &&
            groupDate.getMonth() === targetMonth &&
            groupDate.getFullYear() === targetYear
          ) {
            relevantGroups.push({
              element: element as HTMLElement,
              date: groupDate,
            });
          }
        } catch (error) {
          console.warn('Error parsing group key:', groupKey, error);
        }
      });

      // Sort by date descending (most recent first)
      relevantGroups.sort((a, b) => b.date.getTime() - a.date.getTime());

      // Get the most recent group from the target month
      const mostRecentGroup = relevantGroups[0];

      if (mostRecentGroup?.element) {
        // When in grid view, offset is HeaderBar height (84.5px) + margin (16px) - shadow (3px)
        // When in table view, offset is HeaderBar height (84.5px)
        const headerOffset = viewMode === 'grid' ? 97.5 : 84.5;
        const elementPosition =
          mostRecentGroup.element.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: elementPosition - headerOffset,
          behavior: 'smooth',
        });
      }
    },
    [viewMode]
  );

  const isFiltered =
    filters.category !== '' ||
    filters.month !== '' ||
    filters.year !== '' ||
    filters.status !== '';

  // Smart truncation for search results to prioritize highlighted content
  // This function truncates text to show content from the last word before
  // the first highlighted content onwards, letting CSS handle overflow truncation
  const smartTruncateWithHighlight = useCallback((message: string | any) => {
    if (!message) return '';

    // Safety check to ensure we're working with a string
    if (typeof message !== 'string') {
      try {
        if (Array.isArray(message)) {
          message = (message as any[]).join(' ');
        } else if (typeof message === 'object') {
          const textParts: string[] = [];
          // Extract text parts if this is a content array
          if (Array.isArray((message as any).content)) {
            (message as any).content.forEach((part: any) => {
              if (part.type === 'text' && part.text) {
                textParts.push(part.text);
              }
            });
            message = textParts.join(' ');
          } else {
            // Last resort, try JSON stringify (but not for [object Object])
            message = JSON.stringify(message);
            if (
              message === '{}' ||
              message === '[]' ||
              message.includes('[object Object]')
            ) {
              return '';
            }
          }
        }
      } catch (error) {
        console.warn('Error processing message for display:', error);
        return '';
      }
    }

    // Only apply smart truncation if there's highlighting
    if (message.includes('**')) {
      // Find all highlighted parts
      const highlightMatches = message.match(/\*\*[^*]+\*\*/g);

      if (highlightMatches && highlightMatches.length > 0) {
        const firstHighlight = highlightMatches[0];
        const highlightIndex = message.indexOf(firstHighlight);

        // Split message into words to find word boundaries
        const words = message.split(' ');
        let highlightWordIndex = -1;
        let runningLength = 0;

        // Find which word contains the highlight
        for (let i = 0; i < words.length; i++) {
          if (runningLength + words[i].length >= highlightIndex) {
            highlightWordIndex = i;
            break;
          }
          runningLength += words[i].length + 1; // +1 for space
        }

        // If highlight is found and it's not in the first word, truncate to show context
        if (highlightWordIndex > 0) {
          // Start from the word before the highlighted word to provide context
          const startWordIndex = Math.max(0, highlightWordIndex - 1);
          const truncatedWords = words.slice(startWordIndex);

          // Join the words and add ellipsis to indicate truncation
          return '...' + truncatedWords.join(' ');
        }
      }
    }

    // For messages without highlighting, return the original message
    // CSS will handle the truncation with WebkitLineClamp
    return message;
  }, []);

  return (
    <ThemeProvider theme={NodeTheme}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          visibility: isLoading ? 'hidden' : 'visible',
          mx: { xs: 2, sm: 3 },
        }}
      >
        {/* Search Bar and Filters Button */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            width: '100%',
          }}
        >
          <SearchBar
            placeholder={t('Search entries...')}
            onResultClick={handleSearchResultClick}
            onSearchResultsChange={handleSearchResultsChange}
            maxResults={500}
            debounceMs={300}
          />
          <Button
            variant="floating"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{
              height: '56px', // Match TextField default height
              minWidth: '48px',
              px: 1,
              '& .MuiButton-startIcon': {
                margin: 0,
              },
              '@media (min-width: 450px)': {
                minWidth: '120px',
                px: 2,
                '& .MuiButton-startIcon': {
                  margin: '0 8px 0 0',
                },
              },
            }}
          >
            <Box
              sx={{
                display: 'none',
                '@media (min-width: 450px)': {
                  display: 'block',
                },
              }}
            >
              {t('Filters')}
            </Box>
          </Button>
        </Box>

        {/* Filters Section */}
        <Collapse in={showFilters} unmountOnExit>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              alignItems: 'center',
              backgroundColor: '#F8F8FB',
              p: 2,
              borderRadius: '8px',
              border: '1px solid rgba(56, 39, 103, 0.1)',
            }}
          >
            <FormControl
              sx={{
                minWidth: { xs: '100%', sm: 200 },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <InputLabel>{t('Category')}</InputLabel>
              <Select
                value={filters.category}
                label={t('Category')}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    category: e.target.value as keyof typeof CATEGORIES,
                  }))
                }
              >
                <MenuItem value="">{t('All Categories')}</MenuItem>
                {Object.entries(CATEGORIES).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {t(label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              sx={{
                minWidth: { xs: '100%', sm: 120 },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <InputLabel>{t('Month')}</InputLabel>
              <Select
                value={filters.month}
                label={t('Month')}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, month: e.target.value }))
                }
              >
                <MenuItem value="">{t('All Months')}</MenuItem>
                {months.map((month) => (
                  <MenuItem key={month} value={month}>
                    {format(new Date(2024, month - 1), 'MMMM', {
                      locale: dateLocale,
                    })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              sx={{
                minWidth: { xs: '100%', sm: 120 },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <InputLabel>{t('Year')}</InputLabel>
              <Select
                value={filters.year}
                label={t('Year')}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, year: e.target.value }))
                }
              >
                <MenuItem value="">{t('All Years')}</MenuItem>
                {years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              sx={{
                minWidth: { xs: '100%', sm: 120 },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <InputLabel>{t('Status')}</InputLabel>
              <Select
                value={filters.status}
                label={t('Status')}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, status: e.target.value }))
                }
              >
                <MenuItem value="">{t('ALL_STATUSES')}</MenuItem>
                <MenuItem value="NEW">{t('NEW')}</MenuItem>
                <MenuItem value="IN_PROGRESS">{t('IN_PROGRESS')}</MenuItem>
                <MenuItem value="COMPLETED">{t('COMPLETED')}</MenuItem>
                <MenuItem value="PENDING">{t('PENDING')}</MenuItem>
              </Select>
            </FormControl>

            {isFiltered && (
              <Box sx={{ minWidth: { xs: '100%', sm: 120 } }}>
                <Box>
                  <Tooltip title={t('Reset all filters')}>
                    <Button
                      variant="floating"
                      startIcon={<RestartAltIcon />}
                      onClick={handleResetFilters}
                      sx={{
                        width: { xs: '100%', sm: 'auto' },
                      }}
                    >
                      {t('Reset')}
                    </Button>
                  </Tooltip>
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: '100%',
            justifyContent: 'flex-end',
          }}
        >
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              gap: 1,
              alignItems: 'center',
            }}
          >
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              aria-label="view mode"
              size="small"
              sx={{
                backgroundColor: '#FFFFFF',
                '& .MuiToggleButton-root': {
                  borderColor: '#382767',
                  color: '#382767',
                  '&:first-of-type': {
                    borderTopLeftRadius: '8px',
                    borderBottomLeftRadius: '8px',
                  },
                  '&:last-of-type': {
                    borderTopRightRadius: '8px',
                    borderBottomRightRadius: '8px',
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#382767',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#382767',
                      opacity: 0.9,
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(56, 39, 103, 0.04)',
                  },
                  transition: 'all 0.2s ease',
                },
              }}
            >
              <ToggleButton value="grid" aria-label="grid view">
                <Tooltip title={t('Grid View')}>
                  <ViewModuleIcon />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="table" aria-label="table view">
                <Tooltip title={t('Table View')}>
                  <ViewListIcon />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              width: '100%',
              justifyContent: 'flex-end',
            }}
          >
            <Tooltip title={t('Refresh')}>
              <span>
                <Button
                  variant="floating"
                  startIcon={
                    isRefetching ? (
                      <CircularProgress size={20} sx={{ color: '#382767' }} />
                    ) : (
                      <RefreshIcon />
                    )
                  }
                  onClick={refetch}
                  disabled={isRefetching}
                  sx={{
                    '&:hover': {
                      transform: isRefetching ? 'none' : 'translateY(-2px)',
                    },
                  }}
                >
                  {isRefetching ? t('Refreshing...') : t('Refresh')}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Results count */}
      {(isFiltered || searchQuery) && (
        <Typography
          variant="body2"
          sx={{
            visibility: isLoading ? 'hidden' : 'visible',
            px: { xs: 2, sm: 3 },
            py: 1,
            color: '#666',
            fontFamily: 'Inter',
          }}
        >
          {`${t('Showing')} ${sortedDocuments.length} ${sortedDocuments.length === 1 ? t('1-result') : t('n-results')}`}
        </Typography>
      )}

      {/* Month Quick Filters */}
      <Box
        sx={{
          visibility: isLoading ? 'hidden' : 'visible',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          px: { xs: 2, sm: 3 },
          py: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: { xs: 'nowrap', sm: 'wrap' },
            width: { xs: 'max-content', sm: '100%' },
          }}
        >
          {Array.from(
            new Set(
              sortedDocuments.map((doc) => {
                const date = getValidDate(doc);
                return `${date.getMonth()}-${date.getFullYear()}`;
              })
            )
          )
            .sort((a, b) => {
              const [monthA, yearA] = a.split('-').map(Number);
              const [monthB, yearB] = b.split('-').map(Number);
              return yearB - yearA || monthB - monthA;
            })
            .map((monthYear) => {
              const [month, year] = monthYear.split('-').map(Number);
              return (
                <Button
                  key={monthYear}
                  variant="floating"
                  size="small"
                  onClick={() => scrollToMonth(month, year)}
                  sx={{
                    flex: '0 0 auto',
                    px: 1.5,
                    py: 0.5,
                    minHeight: '32px',
                    fontSize: '0.875rem',
                  }}
                >
                  {`${monthNames[month]} ${year}`}
                </Button>
              );
            })}
        </Box>
      </Box>

      {viewMode === 'table' ? (
        <TableContainer
          component={Paper}
          sx={{
            transition: 'box-shadow 0.3s ease',
            backgroundColor: '#F8F8FB',
            overflowX: 'auto',
            border: 'none',
            padding: 0,
            mx: 3,
            mb: 3,
            width: `calc(100% - ${24 * 2}px)`,
            display: { xs: 'none', sm: 'block' }, // Hide table on mobile
            '& .MuiTable-root': {
              borderSpacing: 0,
              borderCollapse: 'collapse',
            },
            '& .MuiPaper-root': {
              border: 'none',
              padding: 0,
            },
          }}
        >
          <Table>
            <TableHead sx={{ backgroundColor: '#382767' }}>
              <TableRow>
                {['Image', 'Date', 'Title', 'Category', 'Status', 'Amount'].map(
                  (header) => (
                    <TableCell
                      key={header}
                      sx={{ color: 'white', borderBottom: 'none' }}
                    >
                      {header}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedDocuments.map((group) => (
                <React.Fragment key={group.key}>
                  <TableRow data-group-key={group.key}>
                    <TableCell
                      colSpan={6}
                      sx={{
                        backgroundColor: '#f5f5f5',
                        color: '#666',
                        borderBottom: '1px solid #e0e0e0',
                        py: 1,
                      }}
                    >
                      <Typography
                        data-group-key={group.key}
                        variant="body1"
                        sx={{
                          gridColumn: '1 / -1',
                          color: '#444',
                          fontWeight: 400,
                          fontSize: '1.125rem',
                          fontFamily: 'Inter',
                        }}
                      >
                        {group.label}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {group.items.map((document) => (
                    <TableRow
                      key={document.id}
                      hover
                      onClick={() => onRowClick(document)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          transition: 'background-color 0.2s ease',
                        },
                        '& td': {
                          padding: 2,
                          borderColor: 'rgba(224, 224, 224, 0.4)',
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          whiteSpace: { xs: 'normal', sm: 'nowrap' }, // Allow date to wrap on mobile
                          width: { xs: 'max-content', sm: 'auto' },
                          minWidth: { xs: '85px', sm: 'auto' },
                          padding: { xs: '12px 6px', sm: 2 }, // Increased vertical padding on mobile
                          fontSize: { xs: '0.8125rem' }, // Smaller font only on mobile
                          pl: { xs: '8px', sm: 2 }, // Slightly more left padding on first visible column
                          position: 'relative',
                          '&::before':
                            document.status === 'PENDING'
                              ? {
                                  content: '""',
                                  position: 'absolute',
                                  left: 0,
                                  top: '8px',
                                  bottom: '8px',
                                  width: '4px',
                                  backgroundColor: '#FF5733',
                                  borderRadius: '0 4px 4px 0',
                                }
                              : {},
                        }}
                      >
                        {format(getValidDate(document), 'dd.MM.yyyy HH:mm')}
                      </TableCell>
                      <TableCell
                        sx={{
                          display: { xs: 'none', sm: 'table-cell' },
                        }}
                      >
                        {document.content?.[0]?.path ? (
                          <Box
                            sx={{
                              position: 'relative',
                              width: '180px',
                              height: '240px',
                              '&:hover .download-overlay': {
                                opacity: 1,
                              },
                              '&:hover img': {
                                transform: 'scale(1.02)',
                                boxShadow: '0 2px 3px rgba(0,0,0,0.2)',
                              },
                            }}
                          >
                            {!imageUrls[document.id] ? (
                              <Box
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                  borderRadius: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  animation: 'pulse 1.5s ease-in-out infinite',
                                  '@keyframes pulse': {
                                    '0%': { opacity: 0.6 },
                                    '50%': { opacity: 0.8 },
                                    '100%': { opacity: 0.6 },
                                  },
                                }}
                              >
                                <CircularProgress
                                  size={40}
                                  sx={{ color: 'rgba(0, 0, 0, 0.2)' }}
                                />
                              </Box>
                            ) : (
                              <>
                                <img
                                  src={imageUrls[document.id]?.thumbnail || ''}
                                  alt="Receipt"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    transition: 'all 0.3s ease',
                                    opacity: imageUrls[document.id]?.thumbnail
                                      ? 1
                                      : 0,
                                    transform: 'scale(0.98)',
                                    animation: imageUrls[document.id]?.thumbnail
                                      ? 'fadeIn 0.5s ease-in-out forwards'
                                      : 'none',
                                    borderRadius: '8px',
                                  }}
                                  onError={(e) => {
                                    console.error(
                                      `[Image Display] Failed to load image for document ${document.id}`
                                    );
                                    (
                                      e.target as HTMLImageElement
                                    ).style.display = 'none';
                                  }}
                                />
                                <Box
                                  className="download-overlay"
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 2,
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    opacity: 0,
                                    transition: 'opacity 0.3s ease',
                                    borderRadius: 1,
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Tooltip title={t('View full size')}>
                                    <IconButton
                                      sx={{
                                        width: { xs: 28, sm: 32, md: 36 },
                                        height: { xs: 28, sm: 32, md: 36 },
                                        backgroundColor:
                                          'rgba(255, 255, 255, 0.92)',
                                        '&:hover': {
                                          backgroundColor: 'white',
                                          transform: 'scale(1.05)',
                                        },
                                        transition: 'all 0.15s ease',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                                      }}
                                      onClick={() => handleImageOpen(document)}
                                    >
                                      <ZoomInIcon
                                        sx={{
                                          fontSize: { xs: 16, sm: 18, md: 20 },
                                        }}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </>
                            )}
                          </Box>
                        ) : null}
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: { xs: '1px', sm: '200px', md: '300px' }, // Force title to take remaining space
                          width: { xs: '100%', sm: 'auto' },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          padding: { xs: '12px 6px', sm: 2 }, // Increased vertical padding on mobile
                          fontSize: { xs: '0.8125rem' }, // Smaller font only on mobile
                          fontWeight: isDocumentUnread(document) ? 700 : 500, // Bold for unread items
                        }}
                      >
                        <span
                          dangerouslySetInnerHTML={{
                            __html: document.highlightedTitle
                              ? parseHighlightedText(document.highlightedTitle)
                              : document.title || t('New chat'),
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          display: { xs: 'none', sm: 'table-cell' },
                          fontSize: { xs: '0.8125rem' }, // Smaller font only on mobile
                        }}
                      >
                        {document.category
                          ? t(CATEGORIES[document.category])
                          : t(CATEGORIES.other)}
                      </TableCell>
                      <TableCell
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          justifyContent: { xs: 'flex-end', sm: 'flex-start' }, // Right align status on mobile
                          position: 'relative',
                        }}
                      >
                        {document.status === 'IN_PROGRESS'
                          ? '🔄'
                          : document.status === 'COMPLETED'
                            ? '✅'
                            : document.status === 'PENDING'
                              ? '⏳'
                              : '📝'}
                        <span>{t(document.status || 'NEW')}</span>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          display: { xs: 'none', sm: 'table-cell' },
                          whiteSpace: 'nowrap',
                          color: document.amount
                            ? document.amount > 0
                              ? '#4CAF50'
                              : '#FF5733'
                            : 'inherit',
                          fontWeight: document.amount ? 500 : 'inherit',
                        }}
                      >
                        {document.amount &&
                          new Intl.NumberFormat(i18n.language, {
                            style: 'currency',
                            currency: 'EUR',
                          }).format(document.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(auto-fit, minmax(350px, 1fr))',
            },
            gap: 2,
            mx: { xs: 2, sm: 3 },
            mb: { xs: 2, sm: 3 },
          }}
        >
          {groupedDocuments.map((group) => (
            <React.Fragment key={group.key}>
              <Typography
                data-group-key={group.key}
                variant="body1"
                sx={{
                  gridColumn: '1 / -1',
                  color: '#444',
                  fontWeight: 400,
                  fontSize: '1.125rem',
                  fontFamily: 'Inter',
                }}
              >
                {group.label}
              </Typography>
              {group.items.map((document) => (
                <DocumentGridItem
                  key={document.id}
                  document={document as any}
                  onRowClick={onRowClick}
                  onImageOpen={handleImageOpen}
                  imageUrls={imageUrls}
                  getValidDate={getValidDate}
                  smartTruncate={smartTruncateWithHighlight}
                />
              ))}
            </React.Fragment>
          ))}
        </Box>
      )}

      <FullSizePreview
        open={!!selectedImage}
        onClose={handleImageClose}
        imageUrl={selectedImage}
        isLoading={isLoadingFullSize}
        isPDF={
          selectedDocument?.content?.[0]?.path
            ? isPDF(selectedDocument.content[0].path)
            : false
        }
      />
    </ThemeProvider>
  );
};

export default DocumentsTable;
